import { createContext, useState, useCallback, useEffect } from 'react';
import * as React from 'react';
import { AnimatePresence } from 'motion/react';
import Snackbar, { type SnackbarVariant } from 'components/library/Snackbar';
import { getFromSessionStorage } from 'lib/utils/get-from-session-storage';
import { setToSessionStorage } from 'lib/utils/set-to-session-storage';

type SnackbarOptions = {
  message: string;
  variant?: SnackbarVariant;
  duration?: number;
};

type SnackbarContextType = {
  showSnackbar: (options: SnackbarOptions) => void;
};

export const SnackbarContext = createContext<SnackbarContextType>({
  showSnackbar: () => {},
});

type SnackbarProviderProps = {
  children: React.ReactNode;
};

export const SnackbarProvider = ({ children }: SnackbarProviderProps) => {
  const [snackbar, setSnackbar] = useState<SnackbarOptions | null>(null);
  const [snackbarKey, setSnackbarKey] = useState(0);

  const showSnackbar = useCallback((options: SnackbarOptions) => {
    setSnackbar(options);
    setSnackbarKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    const pending = getFromSessionStorage('pendingSnackbar');
    if (pending) {
      setToSessionStorage('pendingSnackbar');
      showSnackbar(pending);
    }
  }, []);

  const handleDismiss = useCallback(() => {
    setSnackbar(null);
  }, []);

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <AnimatePresence>
        {snackbar && (
          <Snackbar
            key={snackbarKey}
            message={snackbar.message}
            variant={snackbar.variant}
            duration={snackbar.duration}
            onDismiss={handleDismiss}
          />
        )}
      </AnimatePresence>
    </SnackbarContext.Provider>
  );
};
